import { FC } from "react";

import { CircleCross } from "@/assets/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PromptTemplate } from "@/types/agenticAI";

import SuggestedPromptCard from "./SuggestedPromptCard";

type Props = {
  prompts: PromptTemplate[];
  onSelect: (prompt: PromptTemplate) => void;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

const GRID_CLASS_NAME = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4";
const SKELETON_CARD_COUNT = 4;

const SuggestedPromptCardSkeleton = () => (
  <div className="flex h-full flex-col gap-3 rounded-xl bg-white p-[18px]">
    <div className="size-6 animate-pulse rounded-full bg-whisper" />
    <div className="h-4 w-2/3 animate-pulse rounded bg-whisper" />
    <div className="flex flex-col gap-2">
      <div className="h-3 w-full animate-pulse rounded bg-whisper" />
      <div className="h-3 w-5/6 animate-pulse rounded bg-whisper" />
      <div className="h-3 w-3/4 animate-pulse rounded bg-whisper" />
    </div>
  </div>
);

const SuggestedPrompts: FC<Props> = ({
  prompts,
  onSelect,
  disabled,
  isLoading,
  isError,
  onRetry
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <ul
          className={GRID_CLASS_NAME}
          data-testid="agentic-ai-suggested-prompts-loading"
        >
          {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
            <li key={index + 1}>
              <SuggestedPromptCardSkeleton />
            </li>
          ))}
        </ul>
      );
    }

    if (isError) {
      return (
        <Alert className="flex gap-3 border-cinnabar bg-red-50 p-4">
          <CircleCross
            aria-hidden
            className="size-5 shrink-0 fill-cinnabar"
            width={20}
            height={20}
          />
          <div>
            <AlertTitle className="text-base font-semibold text-txtHeading">
              Unable to load suggestions
            </AlertTitle>
            <AlertDescription className="text-txtBody">
              You can still ask Agentic AI anything using the search above.
            </AlertDescription>
            <button
              type="button"
              onClick={onRetry}
              className="mt-1 text-sm font-semibold text-royal-blue hover:underline"
            >
              Retry
            </button>
          </div>
        </Alert>
      );
    }

    return (
      <ul className={GRID_CLASS_NAME}>
        {prompts.map((prompt) => (
          <li key={prompt.id}>
            <SuggestedPromptCard
              title={prompt.title}
              description={prompt.prompt_text}
              icon={prompt.icon}
              disabled={disabled}
              onSelect={() => onSelect(prompt)}
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section aria-labelledby="agentic-ai-suggested-prompts" className="w-full">
      <div
        id="agentic-ai-suggested-prompts"
        className="mb-6 text-base font-bold text-txtHeading"
      >
        Suggested prompts
      </div>
      {renderContent()}
    </section>
  );
};

export default SuggestedPrompts;
